import { GEAR_TITLE_MAP, type GEAR } from '@/utils/constants';
import { actingViolations, type EnforcementAction } from '@/utils/tone-policy/enforcement';
import { RULES, type PolicyRule, type Violation } from '@/utils/tone-policy/types';

// What a creator reads when a tone is made private, in the email and the profile modal. Static per
// rule: creators never see the model's evidence or notes.

export const GUIDELINES_URL = 'https://www.tone3000.com/guides/tone-sharing-guidelines';
export const NOTICE_SUBJECT_MARKER = '[Tone policy]';

export const RULE_COPY: Record<PolicyRule, string> = {
  'current-plugin':
    "This tone appears to be a capture of an audio plugin or amp sim. Captures of current plugins, free or paid, aren't allowed.",
  'plugin-in-chain':
    'Part of the signal chain appears to be an audio plugin or amp sim that is still sold or supported. Recapture it with real gear in its place.',
  'unidentified-plugin':
    "This tone says it was made with a plugin, amp sim, or software preset, but doesn't name it, so we can't check that it's allowed.",
  'unclear-gear':
    "We couldn't tell what gear was captured. Name the brand and model of the gear in the title or description.",
  'current-digital-gear':
    'This tone appears to capture a current modeler, such as an amp modeler, profiler, multi-FX unit, or amp or cab modeling pedal that is still in production, or uses one in its chain.',
  'current-cab-sim':
    "The cab appears to come from a current product's cab library, such as Two Notes DynIR. Use a cab you captured yourself or an openly licensed IR.",
  'commercial-ir':
    "The IRs in this pack appear to come from a commercial IR pack. Commercial IRs can't be shared.",
  'commercial-ir-in-chain':
    'The cab appears to be an IR from a commercial IR pack. Use a cab you captured yourself or an openly licensed IR.',
  'all-plugin-chain':
    'The whole signal chain appears to be current plugins. Captures need real gear as their focus.',
  'not-real-gear':
    "The listing says the tone wasn't captured from real gear, for example AI-generated audio.",
  'stated-source':
    'The listing says these captures or IRs came from someone else. Only share captures you made.',
  'harmful-content': "The listing's text or image contains content that isn't allowed on TONE3000.",
  'not-a-photo':
    "The image isn't a real photo of your gear. Screenshots, renders, AI images, stock or web photos, and graphics aren't allowed.",
  'wrong-gear':
    'The image shows different gear than the pack describes. Use a photo of the gear you captured.',
  'needs-diagram': 'Circuit models need a circuit diagram or PCB image as the main image.',
  'needs-no-image':
    "Captures of abandoned plugins and other software models shouldn't have an image.",
  'several-gear':
    'The pack holds gear captured separately that was never chained together. Each pack should hold one amp, pedal, or unit, or one chain of pedals or units.',
  'wrong-pack-type':
    "A capture in this pack doesn't match the pack's gear type. Move it to a pack of the right type, such as an Amp Head pack for captures with no cab.",
  'amp-through-cabs':
    'The pack runs one amp through different cabs. Each Amp + Cab pack should use one cab.',
  'genre-or-artist-pack':
    'The pack groups tones by genre or artist without one main piece of gear.',
  other: "This tone doesn't appear to be a capture of gear, a space, or an object.",
};

export interface PolicyNotice {
  toneId: number;
  toneTitle: string;
  reasons: string[];
  strike: boolean;
  activeStrikes: number;
  action: EnforcementAction;
}

/** The rules that made the tone private, once each, in policy order. */
export const noticeRules = (
  violations: readonly Violation[],
  moderatorConfirmed: boolean
): PolicyRule[] => {
  const acted = new Set(actingViolations(violations, moderatorConfirmed).map((v) => v.rule));
  return RULES.filter((rule) => acted.has(rule));
};

/** Names the declared gear type and the one the captures belong in, when the classifier gave it. */
export function wrongPackTypeCopy(declared: GEAR | null, belongsIn: GEAR | null): string {
  if (!declared || !belongsIn || declared === belongsIn) return RULE_COPY['wrong-pack-type'];
  const target = GEAR_TITLE_MAP[belongsIn];
  return `This pack is listed as ${GEAR_TITLE_MAP[declared]}, but some of its captures are ${target} captures. Move them to a separate ${target} pack.`;
}

/** What the creator reads for each rule that made the tone private, in policy order. */
export function noticeReasons(
  violations: readonly Violation[],
  moderatorConfirmed: boolean,
  declared: GEAR | null
): string[] {
  const acting = actingViolations(violations, moderatorConfirmed);
  return noticeRules(violations, moderatorConfirmed).map((rule) =>
    rule === 'wrong-pack-type'
      ? wrongPackTypeCopy(declared, acting.find((v) => v.rule === rule && v.gear_type)?.gear_type ?? null)
      : RULE_COPY[rule]
  );
}

/** Moderation email subjects carry the marker, so the support bot leaves replies to a person. */
export const markedSubject = (subject: string): string =>
  subject.includes(NOTICE_SUBJECT_MARKER) ? subject : `${NOTICE_SUBJECT_MARKER} ${subject}`;

export const noticeSubject = (toneTitle: string, action: EnforcementAction): string =>
  markedSubject(
    action === 'remove_image'
      ? `We removed the image from your tone "${toneTitle}"`
      : `Your tone "${toneTitle}" was made private`
  );

/** Active strikes that block publishing; publish_tone enforces the same number. */
export const STRIKE_LIMIT = 3;

/** The account's strike status, said once: in the email's strike line and at the top of the modal. */
export function accountStrikeText(activeStrikes: number): string {
  const remaining = STRIKE_LIMIT - activeStrikes;
  if (remaining <= 0) {
    return 'You have no strikes remaining, so you can no longer publish tones. Your tones that are already public stay up, and you can still edit them. A strike is removed if a moderator finds it was a mistake.';
  }
  return `You have ${remaining} ${remaining === 1 ? 'strike' : 'strikes'} remaining before you can no longer publish tones.`;
}

const STRIKE_POLICY_TEXT =
  'We have a three-strike policy. After three strikes, you can no longer publish tones.';
const NO_STRIKE_TEXT = "It does not count as a strike. Once it's fixed, you can publish it again.";
const IMAGE_REMOVED_TEXT =
  'Your tone is still public and this does not count as a strike. You can add a new photo of the gear you captured at any time.';
const CHECK_TEXT =
  'This check runs when you publish a tone or when you edit an existing public one.';

/** strike marks the line both channels emphasize. */
export interface NoticeLine {
  text: string;
  strike: boolean;
}

/** One tone per email: what happened, why, the escalation, and how to dispute. */
export function emailParagraphs(notice: PolicyNotice): NoticeLine[] {
  const line = (text: string, strike = false): NoticeLine => ({ text, strike });
  const removedImage = notice.action === 'remove_image';
  return [
    line(
      removedImage
        ? `We removed the image from your tone "${notice.toneTitle}" because it doesn't follow our Tone Sharing Guidelines.`
        : `We made your tone "${notice.toneTitle}" private because it doesn't follow our Tone Sharing Guidelines.`
    ),
    ...notice.reasons.map((reason) => line(reason)),
    ...(removedImage
      ? [line(IMAGE_REMOVED_TEXT)]
      : notice.strike
        ? [
            line(
              `This counts as a strike on your account. ${accountStrikeText(notice.activeStrikes)}`,
              true
            ),
            line(STRIKE_POLICY_TEXT),
          ]
        : [line(NO_STRIKE_TEXT)]),
    line(CHECK_TEXT),
    line('If you think we got this wrong, reply to this email and a moderator will review it.'),
  ];
}

/** The profile modal states the shared lines once and breaks each tone down underneath. */
export const MODAL_COPY = {
  title: (tones: number) => (tones === 1 ? 'Your tone was flagged' : 'Your tones were flagged'),
  summary: (tones: number) =>
    tones === 1
      ? "Our automatic moderation system flagged one of your tones because it doesn't follow the Tone Sharing Guidelines."
      : `Our automatic moderation system flagged ${tones} of your tones because they don't follow the Tone Sharing Guidelines.`,
  outcome: (strike: boolean, action: EnforcementAction) =>
    action === 'remove_image'
      ? `We removed this tone's image. ${IMAGE_REMOVED_TEXT}`
      : strike
        ? 'This tone was made private, and it counts as a strike.'
        : `This tone was made private. ${NO_STRIKE_TEXT}`,
  howItWorks: `${CHECK_TEXT} If you think we got this wrong, reply to the email we sent you about the tone.`,
};
